import {appConfig} from '@/config/appConfig';
import {debug} from '@/shared/helpers/debug';
import {App} from '@capacitor/app';
import {Browser} from '@capacitor/browser';
import type {Plugin} from 'vue';
import {useRouter} from 'vue-router';

/**
 * Configures handling of hybrid app specific global functionality:
 * - Handling of authentication redirects (MSAL and OIDC)
 * - Handling of universal/app links (https deep links)
 *
 * Supported URL formats:
 * - Unified scheme: dev.deskly.app://msauth#code=... or dev.deskly.app://login#code=...
 * - Universal link MSAL callback: https://app.desk.ly/#code=...
 */
export const hybridAppPlugin = {
    install(app) {
        if (!appConfig.isHybridApp) {
            return;
        }
        app.runWithContext(() => {
            const router = useRouter();

            App.addListener('appUrlOpen', async data => {
                debug.log('App opened with URL:', data.url);

                // Check for unified scheme format: dev.deskly.app://msauth or dev.deskly.app://login
                if (data.url.includes('://msauth')) {
                    await Browser.close();
                    return handleMsalAuthenticationRedirect(data.url);
                }

                if (data.url.includes('://login')) {
                    handleOidcAuthenticationRedirect(data.url);
                    return;
                }

                if (data.url.startsWith('https://')) {
                    const parsedUrl = new URL(data.url);
                    // Detect MSAL callback: hash contains code= (authorization code flow)
                    if (parsedUrl.hash.includes('code=')) {
                        // await Browser.close();
                        return handleMsalAuthenticationRedirect(data.url);
                    }
                    handleUniversalLink(data.url);
                }
            });

            function handleMsalAuthenticationRedirect(url: string) {
                const code = url.split('#')[1];
                debug.log('Extracted code from URL:', code);

                router.push({name: 'Home', hash: '#' + code});
            }

            function handleOidcAuthenticationRedirect(url: string) {
                const parsedUrl = new URL(url);
                const queryString = parsedUrl.search;

                debug.log('Redirecting to callback with query string:', queryString);

                const resolved = router.resolve({name: 'CallBack'});
                router.push(resolved.path + queryString);
            }

            function handleUniversalLink(url: string) {
                const parsedUrl = new URL(url);
                const destination = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;

                debug.log('Navigating to universal link path:', destination);

                router.push(destination);
            }
        });
    },
} satisfies Plugin;
